/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useRef, useState } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { useHasBeenOnScreen } from "~/lib/use-has-been-on-screen";
import { Picture } from "~/components/ui/picture";

import ActiveTripExample from "~/components/landing/bento/ActiveTripExample";
import AnimatedCurfewBentoItem from "~/components/landing/bento/AnimatedCurfewBentoItem";
import DrivingHistoryCarousel from "~/components/landing/bento/DrivingHistoryCarousel";
import WeatherAlertExample from "~/components/landing/bento/WeatherAlertExample";
import RainDropEffect from "~/components/landing/bento/RainDropEffect";
import LocationAnimation from "~/components/landing/bento/LocationAnimation";
import GeoZonesAnimation from "~/components/landing/bento/GeoZonesAnimation";
import BentoToggleIcon from "~/components/landing/bento/BentoToggleIcon";
import LottieAnimation from "~/components/landing/bento/LottieAnimation";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const crashedCar = imageUrl("/images/landing/car-impact-white@2x.png");
const accidentDetectionNotification =
  imageUrl("/images/landing/accident-detection-notification.png");
const drivingHabitsAnimationBackground =
  imageUrl("/images/landing/driving-habits-background.jpg");

/**
 * Ported from bouncieweb's `components/Bento/HighlightsGrid.js`.
 *
 * The 8-item animated "bento" grid on the /connecting-family landing page:
 * Location, Speed, Curfew, Driving History, Driving Habits, Accident Detection,
 * Weather Alerts, Geo-Zones. Faithful reproduction of layout, copy, structure,
 * and animations.
 *
 * Grid: 2 columns on mobile with hand-tuned per-item heights (many in vw), then
 * a 3-column × 6-row template at lg+. The original expressed this as a deep
 * Emotion `styled.div` with responsive row/col spans; reproduced here in a
 * scoped <style> keyed off `.cf-hl-*` item classes so the exact spans survive.
 *
 * Shared building blocks from `Bento/BentoComponents.js` are inlined as small
 * helper components below (GridItem variants + the Bento typography).
 *
 * Each item's "info" overlay toggles via its own `BentoToggleIcon`; the
 * scroll-triggered animations use `useHasBeenOnScreen` exactly as the original.
 */

// ---------------------------------------------------------------------------
// BentoComponents.js — shared primitives
// ---------------------------------------------------------------------------

// GridItem base: blueLight3 bg, 20px radius, clipped, relative, responsive padding.
function gridItemBase(extra?: string) {
  return cn(
    "relative overflow-hidden rounded-[20px] p-6 md:p-8 lg:p-9 xl:p-10",
    extra,
  );
}

function BentoGridH3Large({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className={cn(
        "font-display relative pr-8 font-extrabold leading-[1.15em]",
        "text-[9vw] min-[431px]:text-[6vw] sm:text-[33px] lg:text-[38px] xl:text-[40px]",
      )}
    >
      {children}
    </h3>
  );
}

function BentoGridH3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-display text-[22px] font-extrabold md:text-[29px]">
      {children}
    </h3>
  );
}

function BentoGridSubtitleP({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <p className={cn("relative m-0 text-[21px]", className)} style={style}>
      {children}
    </p>
  );
}

// Overlay used by 5 of the items — frosted blue panel that fades in on toggle.
function Overlay({
  show,
  children,
}: {
  show: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 flex flex-col justify-center p-8 text-white",
        "[background:rgba(31,105,242,0.87)] [backdrop-filter:blur(10px)]",
        "transition-[opacity] duration-300 ease-out",
        show ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      {children}
    </div>
  );
}

function OverlaySubtitleP({ children }: { children: React.ReactNode }) {
  return (
    <p className="relative m-0 [font-family:var(--font-sans)] text-[28px] leading-[1.2] font-bold md:text-[32px]">
      {children}
    </p>
  );
}

function CustomP({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 mb-0 text-[16px] md:text-[18px]">{children}</p>
  );
}

// ---------------------------------------------------------------------------
// HighlightsGrid
// ---------------------------------------------------------------------------

export default function HighlightsGrid() {
  // Overlay open/close state
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);
  const [showDrivingOverlay, setShowDrivingOverlay] = useState(false);
  const [showAccidentOverlay, setShowAccidentOverlay] = useState(false);
  const [showWeatherAlertsOverlay, setShowWeatherAlertsOverlay] = useState(false);
  const [showGeoZonesOverlay, setShowGeoZonesOverlay] = useState(false);

  // Scroll-into-view animation triggers
  const locationRef = useRef<HTMLDivElement>(null);
  const locationHasBeenOnScreen = useHasBeenOnScreen(locationRef);

  const drivingHabitsRef = useRef<HTMLDivElement>(null);
  const drivingHabitsHasBeenOnScreen = useHasBeenOnScreen(drivingHabitsRef);

  const drivingHistoryRef = useRef<HTMLDivElement>(null);

  const accidentDetectionRef = useRef<HTMLDivElement>(null);
  const accidentDetectionHasBeenOnScreen = useHasBeenOnScreen(
    accidentDetectionRef,
  );

  const geoZonesRef = useRef<HTMLDivElement>(null);
  const geoZonesHasBeenOnScreen = useHasBeenOnScreen(geoZonesRef);

  return (
    <>
      <style>{GRID_STYLES}</style>

      <div className="cf-highlights-grid">
        {/* ---------------------------------------------------------------- */}
        {/* 1. LOCATION — dark blue gradient */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={locationRef}
          className={gridItemBase("cf-hl-location cf-dark-gradient text-white")}
        >
          <LocationAnimation animate={locationHasBeenOnScreen} />
          <BentoGridH3Large>Location</BentoGridH3Large>
          <BentoGridSubtitleP style={{ color: colors.blueLight5 }}>
            Where it is. Where it&rsquo;s been.
          </BentoGridSubtitleP>

          <Overlay show={showLocationOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>Location is everything.</OverlaySubtitleP>
              <CustomP>
                Whether it&rsquo;s one vehicle or an entire fleet, see them all
                on one map. Bouncie captures each second of the trip, refreshing
                your map instantly as the vehicle drives. Enjoy the view!
              </CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="location"
            clickHandler={() => setShowLocationOverlay((v) => !v)}
            toggleOn={showLocationOverlay}
            className="trigger-gtm-location-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 2. SPEED (ActiveTripExample) — light blue3, centered */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={cn(
            "cf-hl-speed relative overflow-hidden rounded-[20px] text-center",
            "p-3 min-[431px]:p-4 md:pt-2 lg:pt-3 xl:pt-8",
          )}
          style={{ background: colors.blueLight3 }}
        >
          <BentoGridH3>Speed</BentoGridH3>
          <ActiveTripExample />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 3. CURFEW (self-animating) */}
        {/* ---------------------------------------------------------------- */}
        <AnimatedCurfewBentoItem
          className="cf-hl-curfew"
          text="Sarah's Summer Curfew"
        />

        {/* ---------------------------------------------------------------- */}
        {/* 4. DRIVING HISTORY (carousel) — light blue3, centered */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={drivingHistoryRef}
          className={gridItemBase(
            "cf-hl-driving-history text-center lg:p-3",
          )}
          style={{ background: colors.blueLight3 }}
        >
          <BentoGridH3>Driving History</BentoGridH3>
          <DrivingHistoryCarousel parentRef={drivingHistoryRef} />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 5. DRIVING HABITS — lottie overlay + bg image */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={drivingHabitsRef}
          className={gridItemBase(
            "cf-hl-driving-habits flex flex-col justify-end",
          )}
          style={{ background: colors.blueLight3 }}
        >
          {/* DrivingHabitsAnimationWrapper */}
          <div
            className={cn(
              "pointer-events-none absolute right-0 bottom-[-1px] h-fit",
              "w-[130%] min-[431px]:w-full",
              "md:w-[85%] md:[mask-image:linear-gradient(90deg,transparent,black_10%)]",
              "lg:bottom-[-1px] lg:w-[41rem] lg:[mask-image:none]",
              "xl:w-[47rem]",
            )}
          >
            <Picture
              src={drivingHabitsAnimationBackground}
              alt=""
              className="w-full"
            />
            {drivingHabitsHasBeenOnScreen && (
              <LottieAnimation
                path="/landing/driving-habits-animation.json"
                loop={false}
                styles={{
                  position: "absolute",
                  width: "100%",
                  top: 0,
                  left: 0,
                }}
              />
            )}
          </div>
          <BentoGridH3Large>
            Habits
            <br />
            Revealed
          </BentoGridH3Large>
          <BentoGridSubtitleP>Driving change.</BentoGridSubtitleP>
          <Overlay show={showDrivingOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>Informing. Improving.</OverlaySubtitleP>
              <CustomP>
                Bouncie provides timely insights on speed, acceleration,
                braking, and more. Steer towards excellence!
              </CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="driving-habits"
            clickHandler={() => setShowDrivingOverlay((v) => !v)}
            toggleOn={showDrivingOverlay}
            className="trigger-gtm-driving-habits-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 6. ACCIDENT DETECTION — animated car + notification */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={gridItemBase(
            "cf-hl-accident cf-dark-gradient text-white",
          )}
        >
          <style>{ACCIDENT_STYLES}</style>
          <BentoGridH3Large>Accident Detection</BentoGridH3Large>
          <BentoGridSubtitleP style={{ color: colors.blueLight5 }}>
            All cargo is precious.
          </BentoGridSubtitleP>

          {/* CrashedCarImage — slides in from the right on scroll. The ref
              lives on this wrapper div (not the Picture) because Picture
              doesn't forward refs to its inner <img>. */}
          <div
            ref={accidentDetectionRef}
            className={cn(
              "cf-crashed-car absolute h-fit max-w-none",
              accidentDetectionHasBeenOnScreen && "cf-animate-translate",
            )}
          >
            <Picture
              src={crashedCar}
              alt="A car that has been in an accident"
              className="block h-auto w-full max-w-none"
            />
          </div>
          {/* AccidentDetectionNotification — slides in slightly offset */}
          <Picture
            src={accidentDetectionNotification}
            alt="A notification about a detected accident"
            className={cn(
              "cf-accident-notification absolute h-fit max-w-none",
              accidentDetectionHasBeenOnScreen && "cf-animate-translate",
            )}
          />

          <Overlay show={showAccidentOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>Peace of mind.</OverlaySubtitleP>
              <CustomP>
                Advanced sensors and software immediately detect vehicle
                accidents. Bouncie will automatically send an SMS message to your
                notification contacts. Just breathe!
              </CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="accident-detection"
            clickHandler={() => setShowAccidentOverlay((v) => !v)}
            toggleOn={showAccidentOverlay}
            className="trigger-gtm-accident-detection-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 7. WEATHER ALERTS — raindrop-fx + notification */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={gridItemBase(
            "cf-hl-weather flex flex-col justify-start lg:justify-center",
          )}
          style={{ background: colors.blueLight3 }}
        >
          <RainDropEffect />
          {/* WeatherAlertsText */}
          <div className="relative">
            <BentoGridH3Large>
              Weather
              <br />
              Alerts
            </BentoGridH3Large>
            <BentoGridSubtitleP>Eyes in the skies.</BentoGridSubtitleP>
          </div>
          <WeatherAlertExample />
          <Overlay show={showWeatherAlertsOverlay}>
            <OverlaySubtitleP>Weather wise.</OverlaySubtitleP>
            <CustomP>
              We&rsquo;re watching out for you. When a severe weather advisory is
              issued near your vehicle, Bouncie automatically sends a message
              with all the details. We got this!
            </CustomP>
          </Overlay>
          <BentoToggleIcon
            sid="weather-alerts"
            clickHandler={() => setShowWeatherAlertsOverlay((v) => !v)}
            toggleOn={showWeatherAlertsOverlay}
            className="trigger-gtm-weather-alerts-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 8. GEO-ZONES — dark blue gradient + animation */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={geoZonesRef}
          className={gridItemBase(
            "cf-hl-geozones cf-dark-gradient flex flex-col justify-start text-white md:justify-center",
          )}
        >
          <GeoZonesAnimation animate={geoZonesHasBeenOnScreen} />
          <BentoGridH3Large>Geo-Zones</BentoGridH3Large>
          <BentoGridSubtitleP style={{ color: colors.blueLight5 }}>
            Are they there yet?
          </BentoGridSubtitleP>
          <Overlay show={showGeoZonesOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>Know boundaries.</OverlaySubtitleP>
              <CustomP>
                Using Geo-Zones, Bouncie will send a notification when a vehicle
                arrives or when it leaves. Zone in!
              </CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="geo-zones"
            clickHandler={() => setShowGeoZonesOverlay((v) => !v)}
            toggleOn={showGeoZonesOverlay}
            className="trigger-gtm-geo-zones-overlay"
          />
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Grid template + accident keyframes (scoped raw CSS)
// ---------------------------------------------------------------------------

// The dark-blue-gradient background shared by Location / Accident / Geo-Zones.
const DARK_GRADIENT = `linear-gradient(${colors.zachBlack}, ${colors.blueDark4})`;

const GRID_STYLES = `
  .cf-highlights-grid .cf-dark-gradient {
    background: ${DARK_GRADIENT};
  }

  .cf-highlights-grid {
    display: grid;
    gap: 1rem;
    margin-bottom: 2rem;
    grid-template-columns: repeat(2, 1fr);
  }
  .cf-highlights-grid .cf-hl-location { grid-column: span 2; height: 85vw; }
  .cf-highlights-grid .cf-hl-driving-habits { grid-column: span 2; grid-row: 3/4; height: 80vw; }
  .cf-highlights-grid .cf-hl-driving-history { grid-column: span 2; }
  .cf-highlights-grid .cf-hl-accident { grid-column: span 2; height: 90vw; }
  .cf-highlights-grid .cf-hl-weather { grid-column: span 2; height: 20rem; }
  .cf-highlights-grid .cf-hl-geozones { grid-column: span 2; height: 90vw; }

  /* xsm 431 */
  @media (min-width: 431px) {
    .cf-highlights-grid .cf-hl-location { height: 18rem; }
    .cf-highlights-grid .cf-hl-driving-habits { height: 63vw; }
    .cf-highlights-grid .cf-hl-accident { height: 70vw; }
    .cf-highlights-grid .cf-hl-weather { grid-column: span 2; height: 18rem; }
    .cf-highlights-grid .cf-hl-geozones { height: 60vw; }
  }

  /* sm 640 */
  @media (min-width: 640px) {
    .cf-highlights-grid .cf-hl-driving-habits { height: 21.5rem; }
    .cf-highlights-grid .cf-hl-accident { grid-column: span 2; height: 23rem; }
    .cf-highlights-grid .cf-hl-weather { height: 18rem; }
    .cf-highlights-grid .cf-hl-geozones { height: 22rem; }
  }

  /* md 768 */
  @media (min-width: 768px) {
    .cf-highlights-grid { gap: 1.5rem; grid-template-columns: repeat(2, 1fr); }
    .cf-highlights-grid .cf-hl-location { height: 23rem; }
    .cf-highlights-grid .cf-hl-curfew { height: unset; }
    .cf-highlights-grid .cf-hl-driving-habits { height: 23rem; }
    .cf-highlights-grid .cf-hl-accident { height: 20rem; }
    .cf-highlights-grid .cf-hl-weather { height: 15rem; }
    .cf-highlights-grid .cf-hl-geozones { height: 20rem; }
  }

  /* lg 1024 */
  @media (min-width: 1024px) {
    .cf-highlights-grid {
      grid-template-rows: repeat(6, 13em);
      grid-template-columns: repeat(3, 1fr);
    }
    .cf-highlights-grid .cf-hl-location { grid-row: 1/3; grid-column: 1/3; height: unset; }
    .cf-highlights-grid .cf-hl-driving-history { grid-column: unset; height: unset; }
    .cf-highlights-grid .cf-hl-driving-habits { grid-row: 3/5; grid-column: 2/4; height: unset; }
    .cf-highlights-grid .cf-hl-accident { grid-row: 4/6; grid-column: unset; height: unset; }
    .cf-highlights-grid .cf-hl-weather { grid-column: 2/4; grid-row: unset; height: unset; }
    .cf-highlights-grid .cf-hl-geozones { grid-column: span 3; height: unset; }
  }

  /* xlg 1280 */
  @media (min-width: 1280px) {
    .cf-highlights-grid { grid-template-rows: repeat(6, 15rem); }
  }
`;

// Accident Detection: the crashed car and the notification each translate in
// from the right once on screen. Positions differ per breakpoint (verbatim from
// the original CrashedCarImage / AccidentDetectionNotification styled defs).
const ACCIDENT_STYLES = `
  @keyframes cf-accident-car-translate {
    0%   { transform: translate(30px, 0px); }
    100% { transform: translate(0px, 0px); }
  }
  @keyframes cf-accident-notif-translate {
    0%   { transform: translate(50px, 0px); }
    100% { transform: translate(0px, 0px); }
  }

  .cf-crashed-car {
    bottom: 0;
    width: 100%;
    right: -15%;
    transform: translate(30px, 0px);
  }
  .cf-crashed-car.cf-animate-translate {
    animation: cf-accident-car-translate 3s forwards 1 ease-out;
  }
  @media (min-width: 431px) {
    .cf-crashed-car { width: 95%; bottom: -3%; right: -18%; }
  }
  @media (min-width: 768px) {
    .cf-crashed-car { width: 27rem; right: -4rem; }
  }
  @media (min-width: 1024px) {
    .cf-crashed-car { bottom: 0; right: -50%; width: 150%; }
  }

  .cf-accident-notification {
    bottom: 32%;
    width: 65%;
    right: 26%;
    transform: translate(50px, 0px);
  }
  .cf-accident-notification.cf-animate-translate {
    animation: cf-accident-notif-translate 3s forwards 1 ease-out;
  }
  @media (min-width: 431px) {
    .cf-accident-notification { bottom: 36%; right: 30%; }
  }
  @media (min-width: 640px) {
    .cf-accident-notification { width: 55%; right: 35%; bottom: 40%; }
  }
  @media (min-width: 768px) {
    .cf-accident-notification { width: 18rem; bottom: 6.5rem; right: 13.5rem; }
  }
  @media (min-width: 1024px) {
    .cf-accident-notification { right: 2rem; bottom: 9rem; width: 15rem; }
  }
  @media (min-width: 1280px) {
    .cf-accident-notification { width: 16.2rem; bottom: 9.5rem; }
  }
`;
